/**
 * PublishedContractDocsConfig - Aba de documentos exigidos no contrato publicado
 * Permite adicionar/remover tipos de documento e definir obrigatoriedade
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-09
 */
import { useState } from 'react';
import { Plus, Trash2, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDocumentTypes } from '@/hooks/useDocumentTypes';
import { usePublishedContractRequiredDocs } from '@/hooks/usePublishedContractRequiredDocs';

interface PublishedContractDocsConfigProps {
  /** ID do contrato publicado (null se ainda não salvo) */
  publishedContractId?: string;
}

/**
 * Componente de configuração de documentos exigidos
 */
export function PublishedContractDocsConfig({
  publishedContractId,
}: PublishedContractDocsConfigProps) {
  const [selectedTypeId, setSelectedTypeId] = useState('');

  // Tipos de documento disponíveis no tenant
  const { documentTypes, isLoading: loadingTypes } = useDocumentTypes();

  // Documentos já vinculados ao contrato publicado
  const {
    requiredDocs,
    isLoading: loadingDocs,
    addRequiredDoc,
    updateRequiredDoc,
    removeRequiredDoc,
  } = usePublishedContractRequiredDocs(publishedContractId);

  // Tipos já vinculados (para filtrar no select)
  const linkedTypeIds = new Set(requiredDocs.map((d) => d.document_type_id));

  // Tipos disponíveis para adicionar (ativos e não vinculados)
  const availableTypes = documentTypes.filter(
    (dt) => dt.is_active && !linkedTypeIds.has(dt.id)
  );

  // Adicionar tipo de documento ao contrato publicado
  const handleAdd = () => {
    if (!selectedTypeId) return;
    addRequiredDoc.mutate(
      { document_type_id: selectedTypeId, is_required: true },
      { onSuccess: () => setSelectedTypeId('') }
    );
  };

  if (!publishedContractId) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        <FileCheck className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
        <p>Salve o contrato primeiro para configurar documentos exigidos.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">Documentos Exigidos</Label>
        <p className="text-xs text-muted-foreground mt-0.5">
          Defina quais documentos o cliente deve anexar ao gerar este contrato
        </p>
      </div>

      {/* Adicionar tipo de documento */}
      <div className="flex items-center gap-2">
        <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Selecione um tipo de documento..." />
          </SelectTrigger>
          <SelectContent>
            {availableTypes.map((dt) => (
              <SelectItem key={dt.id} value={dt.id}>
                <div className="flex items-center gap-2">
                  <span>{dt.name}</span>
                  <Badge variant="outline" className="text-[10px] h-4">
                    {dt.category}
                  </Badge>
                </div>
              </SelectItem>
            ))}
            {availableTypes.length === 0 && (
              <div className="px-2 py-3 text-xs text-muted-foreground text-center">
                {documentTypes.length === 0
                  ? 'Nenhum tipo cadastrado. Crie tipos em Tipos de Documento.'
                  : 'Todos os tipos já foram adicionados.'}
              </div>
            )}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={handleAdd}
          disabled={!selectedTypeId || addRequiredDoc.isPending}
        >
          <Plus className="h-4 w-4 mr-1" />
          Adicionar
        </Button>
      </div>

      {/* Lista de documentos vinculados */}
      {requiredDocs.length === 0 ? (
        <div className="py-6 text-center text-muted-foreground text-sm border rounded-lg">
          Nenhum documento exigido configurado
        </div>
      ) : (
        <div className="space-y-2">
          {requiredDocs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 border rounded-lg"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileCheck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {doc.document_types?.name || 'Tipo desconhecido'}
                  </p>
                  {doc.document_types?.description && (
                    <p className="text-xs text-muted-foreground truncate">
                      {doc.document_types.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Toggle obrigatório */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Obrigatório</span>
                  <Switch
                    checked={doc.is_required}
                    onCheckedChange={(checked) =>
                      updateRequiredDoc.mutate({ id: doc.id, is_required: checked })
                    }
                  />
                </div>

                {/* Remover */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => removeRequiredDoc.mutate(doc.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
